import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type Slide = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/mnj46yVseiryCw3wJ4hB3u/tomo1_foto.png',
  alt: 'Tomo principal del Kit Medicina Tradicional China',
  title: 'TOMO PRINCIPAL',
  caption: 'Guía Maestra de Protocolos · La base de tu práctica'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/w9ETcwiCSLhUpLC5NiMTVE/Screenshot_20260514-220738~2.png',
  alt: 'Ficha del Paciente - Versión Premium',
  title: 'FICHA DEL PACIENTE — PREMIUM',
  caption: 'Bonus 4 · Plantilla clínica completa, lista para imprimir'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mPhbUQChXeQFCxt3JDdCxv/Screenshot_20260514-220814~2.png',
  alt: 'Caso clínico Cervicalgia y Contracturas - alimentos',
  title: 'CASO 1 · CERVICALGIA',
  caption: 'Bonus 3 · Alimentos por cuadro clínico'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8eaEooc2EnPk9oC9EVp9dt/Screenshot_20260514-220901~2.png',
  alt: 'Caso clínico Ansiedad crónica - plantas e infusiones',
  title: 'CASO 3 · ANSIEDAD CRÓNICA',
  caption: 'Bonus 2 · Hierbas argentinas con criterio local'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/tivxnzvZMJ1YKrqAraoVCA/Screenshot_20260514-221100~2.png',
  alt: 'Punto clave R1 Yongquan - guía de aplicación',
  title: 'PUNTO CLAVE · R1 YONGQUAN',
  caption: 'Tomo Principal · Protocolos paso a paso'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xf6yV5wvsFSEmsBETxy6Xw/Screenshot_20260514-221926~2.png',
  alt: 'Instagram educativo: el contenido que atrae pacientes',
  title: 'INSTAGRAM EDUCATIVO',
  caption: 'Bonus · Contenido que atrae pacientes a tu consultorio'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused]);
  // Touch / swipe support for mobile
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();else prev();
    }
    setTouchStartX(null);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.96
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.96
    })
  };
  return <Section className="max-w-5xl" data-id="element-191">
      <div className="text-center mb-8 md:mb-12" data-id="element-192">
        <div className="inline-flex items-center justify-center bg-white/80 border border-brand-celeste/40 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-193">
          <span className="font-serif font-semibold tracking-wider text-brand-greenDark text-xs md:text-sm" data-id="element-194">
            VISTA PREVIA DEL KIT
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold leading-tight text-brand-greenDark" data-id="element-195">
          📸 Conocé el material por dentro
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-196">
          Tomo principal, bonus y plantillas clínicas — todo diseñado para usar
          en el consultorio desde la primera sesión.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-197">
        <div className="relative rounded-3xl overflow-hidden border border-brand-celeste/30 bg-gradient-to-br from-brand-greenDark/95 via-brand-green/90 to-brand-celesteDark/90 shadow-[0_20px_60px_rgba(15,118,110,0.25)]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-198">
          {/* Decorative texture */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-199"></div>

          <div className="relative w-full flex items-center justify-center px-4 md:px-16 py-8 md:py-12" style={{
          minHeight: '420px'
        }} data-id="element-200">
            <AnimatePresence custom={direction} mode="wait" data-id="element-201">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 260,
                damping: 30
              },
              opacity: {
                duration: 0.3
              },
              scale: {
                duration: 0.3
              }
            }} className="flex flex-col items-center justify-center w-full" data-id="element-202">
                <div className="relative" data-id="element-203">
                  {/* Soft glow behind image */}
                  <div className="absolute -inset-4 bg-white/20 blur-2xl rounded-3xl" data-id="element-204"></div>

                  <img src={slides[index].src} alt={slides[index].alt} draggable={false} className="relative max-h-[420px] md:max-h-[520px] w-auto max-w-full rounded-xl shadow-2xl ring-1 ring-white/20 select-none" data-id="element-205" />
                </div>

                <div className="relative mt-5 md:mt-6 text-center max-w-md" data-id="element-206">
                  <p className="font-display font-bold text-white text-sm md:text-lg tracking-wide drop-shadow" data-id="element-207">
                    {slides[index].title}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-celeste/40 shadow-md flex items-center justify-center text-brand-green hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-208">
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-209" />
          </button>
          <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-celeste/40 shadow-md flex items-center justify-center text-brand-green hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-210">
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-211" />
          </button>
        </div>

        {/* Caption below carousel */}
        <div className="mt-5 text-center px-4 min-h-[2.5rem]" data-id="element-212">
          <AnimatePresence mode="wait" data-id="element-213">
            <motion.p key={index} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.25
          }} className="text-sm md:text-base font-medium text-brand-greenDark" data-id="element-214">
              {slides[index].caption}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-2 mt-4" data-id="element-215">
          {slides.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-brand-green' : 'w-2 bg-brand-green/20 hover:bg-brand-green/50'}`} data-id="element-216" />)}
        </div>
        <p className="text-center text-xs text-brand-muted mt-3" data-id="element-217">
          {index + 1} / {slides.length} · Deslizá o usá las flechas
        </p>
      </div>
    </Section>;
};